import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  Button,
  Box,
  Typography,
  IconButton,
  Slide,
  Chip,
  Avatar
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  CloudDownload as UpdateIcon,
  NewReleases as NewIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components for better visual hierarchy
const StyledAlert = styled(Alert)(({ theme }) => ({
  minWidth: 400,
  maxWidth: 500,
  alignItems: 'center',
  background: 'linear-gradient(135deg, #F7A600 0%, #FFB52E 100%)',
  color: 'white',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(247, 166, 0, 0.3)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  '& .MuiAlert-icon': {
    color: 'white',
    fontSize: '24px'
  },
  '& .MuiAlert-message': {
    width: '100%'
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: '8px 16px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  background: 'rgba(255, 255, 255, 0.2)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  color: 'white',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.4)',
  },
  '&:disabled': {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.6)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  }
}));

const FeatureChip = styled(Chip)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.15)',
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  fontSize: '11px',
  height: '24px',
  '& .MuiChip-label': {
    padding: '0 8px'
  }
}));

const PWAUpdateNotification = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Listen for service worker update events
    const handleUpdateAvailable = (event) => {
      console.log('Update available event received:', event.detail);
      setRegistration(event.detail.registration);
      setUpdateAvailable(true);
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable);

    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable);
    };
  }, []);

  const handleUpdateApp = async () => {
    if (!registration || !registration.waiting) {
      return;
    }

    setIsInstalling(true);

    try {
      // Tell the service worker to skip waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Wait for the new service worker to take control
      await new Promise((resolve) => {
        const handleControllerChange = () => {
          navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
          resolve();
        };
        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
      });

      // Reload the page to get the new version
      window.location.reload();
    } catch (error) {
      console.error('Error updating app:', error);
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setUpdateAvailable(false);
    // Store dismissal in session storage to prevent showing again in this session
    sessionStorage.setItem('update-dismissed', 'true');
  };

  if (!updateAvailable) {
    return null;
  }

  return (
    <Snackbar
      open={updateAvailable}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'down' }}
      sx={{ 
        zIndex: 9999,
        mt: 2
      }}
    >
      <StyledAlert
        severity="info"
        variant="filled"
        icon={<UpdateIcon />}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StyledButton
              onClick={handleUpdateApp}
              disabled={isInstalling}
              startIcon={<RefreshIcon />}
            >
              {isInstalling ? 'Updating...' : 'Update Now'}
            </StyledButton>
            <IconButton
              size="small"
              color="inherit"
              onClick={handleDismiss}
              disabled={isInstalling}
              sx={{ 
                color: 'white',
                opacity: 0.8,
                '&:hover': { 
                  opacity: 1,
                  background: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Avatar
                                   src="/intracapital-logos/intracpiatal-app-icon.png"
              sx={{ width: 32, height: 32 }}
            />
            <Box>
              <Typography variant="body1" fontWeight="bold" sx={{ color: 'white' }}>
                New Version Available! 🚀
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block' }}>
                INTRA CAPITAL has been updated with new features and improvements
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <FeatureChip
              icon={<SpeedIcon sx={{ fontSize: 14 }} />}
              label="Performance"
              size="small"
            />
            <FeatureChip
              icon={<SecurityIcon sx={{ fontSize: 14 }} />}
              label="Security"
              size="small"
            />
            <FeatureChip
              icon={<NewIcon sx={{ fontSize: 14 }} />}
              label="New Features"
              size="small"
            />
          </Box>
        </Box>
      </StyledAlert>
    </Snackbar>
  );
};

export default PWAUpdateNotification; 